import { useState } from "react";
import { Form } from "@/components/shared/forms/Form";
import { useRouteContext } from "@tanstack/react-router";
import { useForm, SubmitHandler } from "react-hook-form";
import { FiEdit, FiEye, FiPlus, FiSave, FiTrash2 } from "react-icons/fi";

import { SASS, SASS_TEMPLATE, sassMutationFunctions } from "@/models/stff";
import { useLeaData } from "@/queries.ts";
import Popup from "@/components/shared/Popup";
import { SASSAddEditForm } from "@/components/records/staff/SASSAddEditForm";
import { useRecordSubmission } from "@/hooks/useRecordSubmission";
import LoaderButton from "@/components/shared/LoaderButton";
import { DeleteConfirmModal } from "@/components/shared/DeleteConfirmModal";

export default function SASSEditDialog({
  record,
  defaults,
  onClose,
  onDeleted,
  viewOnly = false,
}: {
  record?: SASS;
  defaults?: Partial<SASS>;
  onClose: () => void;
  onDeleted?: (record: SASS) => void;
  viewOnly?: boolean;
}) {
  const leaContext = useRouteContext({ from: "/$leaid" });
  const result = useLeaData(leaContext.currentUserOrg.esiIdPath);
  const leaData = result.data;

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const form = useForm<SASS>({
    defaultValues: record ?? { ...SASS_TEMPLATE, ...(defaults || {}) },
  });

  const { isSubmitting, setSubmitResult, handleSubmitStart } =
    useRecordSubmission({
      form,
      recordType: "SASS",
      toastMessage:
        "Staff assignment record submitted.\nWaiting for validation...",
      onSuccess: onClose,
    });

  const {
    isSubmitting: isDeleteSubmitting,
    setSubmitResult: setDeleteSubmitResult,
    handleSubmitStart: handleDeleteStart,
  } = useRecordSubmission({
    form,
    recordType: "SASS",
    toastMessage: "Staff assignment record submitted for deletion",
    successMessage: "Staff assignment record deleted successfully",
    onSuccess: () => {
      if (record && onDeleted) onDeleted(record);
      onClose();
    },
  });

  const mutateFns = sassMutationFunctions(leaData, setSubmitResult);
  const deleteMutateFns = sassMutationFunctions(leaData, setDeleteSubmitResult);

  const onSubmit: SubmitHandler<SASS> = (x) => {
    handleSubmitStart();
    mutateFns.handleSave(x);
  };

  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    if (record) {
      setIsDeleting(true);
      handleDeleteStart();
      deleteMutateFns.handleSave(record, true);
    }
  };

  const canEdit = leaContext.hasRole("SASSEdit");
  const isEdit = !!record;
  const formId = "frmSASSEditDialog";
  const effectiveCanEdit = canEdit && !viewOnly;

  return (
    <>
      <Popup
        show={true}
        enforceFocus={!showDeleteConfirm}
        header={
          isEdit ? (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {effectiveCanEdit ? <FiEdit size={20} /> : <FiEye size={20} />}
              <span>
                {effectiveCanEdit
                  ? "Edit Staff Assignment Record"
                  : "View Staff Assignment Record"}
              </span>
            </div>
          ) : (
            <div
              className="d-flex align-items-center gap-2"
              style={{ fontSize: "1.25rem" }}
            >
              {effectiveCanEdit ? <FiPlus size={20} /> : <FiEye size={20} />}
              <span>
                {effectiveCanEdit
                  ? "Add Staff Assignment Record"
                  : "View Staff Assignment Record"}
              </span>
            </div>
          )
        }
        hasButton={false}
        submitFormId={effectiveCanEdit ? formId : undefined}
        submitButtonText={
          isEdit ? (
            <span className="d-flex align-items-center">
              <FiEdit className="me-1" />
              Update
            </span>
          ) : (
            <span className="d-flex align-items-center">
              <FiSave className="me-1" />
              Save
            </span>
          )
        }
        submitButtonLoading={isSubmitting}
        submitButtonLoadingText="Submitting..."
        secondButtonText="Cancel"
        size="lg"
        onClose={onClose}
        onSecondButtonClick={onClose}
        canEdit={effectiveCanEdit}
        leftFooterButton={
          canEdit && isEdit ? (
            <LoaderButton
              variant="danger"
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              loading={isDeleting && isDeleteSubmitting}
              loadingText="Deleting..."
              className="d-flex align-items-center"
            >
              <FiTrash2 className="me-1" />
              Delete
            </LoaderButton>
          ) : undefined
        }
      >
        <Form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
          <SASSAddEditForm
            selectedStaffAssignment={record}
            form={form}
            sassDefaults={defaults}
          />
        </Form>
      </Popup>

      {showDeleteConfirm && (
        <DeleteConfirmModal
          onCancel={() => setShowDeleteConfirm(false)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </>
  );
}
